<template>
  <v-container grid-list-md>
    <v-layout row wrap>
      <v-flex xs12>
        <v-card>
          <v-toolbar color="primary" dark flat>
            <v-btn icon dark @click="$router.push('/')">
              <v-icon>arrow_back</v-icon>
            </v-btn>
            <v-toolbar-title>League Management</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn small outlined @click="reload()">
              <v-icon left>refresh</v-icon>
              Reload
            </v-btn>
            <v-progress-linear
              :active="isLoading"
              indeterminate
              absolute
              bottom
              color="white"
            ></v-progress-linear>
          </v-toolbar>

          <v-card-text>
            <v-form ref="newLeagueForm" @submit.prevent="createLeague">
              <div class="subtitle-1 mb-2">
                Add division (State → League → Division)
              </div>
              <v-layout row wrap align-center>
                <v-flex xs12 sm4>
                  <v-text-field
                    v-model="newLeagueState"
                    label="State"
                    placeholder="e.g. Illinois"
                    prepend-icon="place"
                  />
                </v-flex>
                <v-flex xs12 sm4>
                  <v-text-field
                    v-model="newLeagueLeague"
                    label="League"
                    placeholder="e.g. IESA, IKWF"
                    prepend-icon="emoji_events"
                  />
                </v-flex>
                <v-flex xs12 sm4>
                  <v-text-field
                    v-model="newLeagueDivision"
                    label="Division"
                    placeholder="e.g. A, AA, Junior"
                    prepend-icon="group_work"
                  />
                </v-flex>
                <v-flex xs12 md4>
                  <v-text-field
                    v-model="newLeagueName"
                    label="Display name (for rankings)"
                    hint="Default: League + Division, e.g. IESA A"
                    persistent-hint
                  />
                </v-flex>
                <v-flex xs12 md3>
                  <file-select v-model="newLeagueLogoFile" />
                </v-flex>
                <v-flex xs12 md5 class="text-md-right">
                  <v-btn
                    color="success"
                    :disabled="!canCreateDivision"
                    @click="createLeague"
                  >
                    <v-icon left>add</v-icon>
                    Add Division
                  </v-btn>
                </v-flex>
              </v-layout>
            </v-form>
          </v-card-text>
        </v-card>
      </v-flex>

      <v-flex xs12 class="mt-3">
        <v-card>
          <v-toolbar color="warning" dark flat>
            <v-icon left>admin_panel_settings</v-icon>
            <v-toolbar-title>Admin Tools</v-toolbar-title>
          </v-toolbar>
          <v-card-text>
            <v-layout row wrap>
              <v-flex xs12>
                <v-btn
                  color="warning"
                  block
                  :loading="globalSkillRecalcLoading"
                  :disabled="globalSkillRecalcLoading"
                  @click="resetAllSkillsGlobalRecalc"
                >
                  <v-icon left>auto_graph</v-icon>
                  Reset &amp; recalc skills (global)
                </v-btn>
                <p class="caption mt-2 mb-0 grey--text text--darken-1">
                  Sets every wrestler to 1200, then replays each unique match
                  once in date order (oldest first). Ranking snapshots and match
                  skill changes are updated. This cannot be undone.
                </p>
              </v-flex>
              <v-flex xs12 class="mt-3">
                <v-btn
                  color="secondary"
                  block
                  :loading="teamRankingsCacheLoading"
                  :disabled="
                    teamRankingsCacheLoading || globalSkillRecalcLoading
                  "
                  @click="rebuildAllTeamRankingsCache"
                >
                  <v-icon left>storage</v-icon>
                  Rebuild team rankings cache (all leagues)
                </v-btn>
                <p class="caption mt-2 mb-0 grey--text text--darken-1">
                  Writes <code>leagueTeamRankingsCache</code> so division Team
                  rankings load from one document read when the snapshot is fresh
                  (~30h). Rebuild after bulk imports or rule changes; a scheduled
                  function can run this daily.
                </p>
              </v-flex>
            </v-layout>
            <v-layout
              v-if="
                globalSkillRecalcLoading ||
                skillRecalcStatus ||
                skillRecalcLastError ||
                skillRecalcProgressPercent > 0
              "
              row
              wrap
              class="mt-3"
            >
              <v-flex xs12>
                <v-card flat class="skill-recalc-progress-card">
                  <v-card-text>
                    <div
                      v-if="skillRecalcLastError"
                      class="skill-recalc-error mb-3 pa-2"
                    >
                      <strong>Stopped:</strong> {{ skillRecalcLastError }}
                    </div>
                    <div
                      v-if="skillRecalcStepLabel"
                      class="skill-recalc-step-label mb-2"
                    >
                      {{ skillRecalcStepLabel }}
                    </div>
                    <div
                      v-if="skillRecalcSubTotal > 0"
                      class="skill-recalc-sub-line mb-2"
                    >
                      {{ skillRecalcSubCaption }}
                      <strong>{{ skillRecalcSubCurrent }}</strong>
                      /
                      {{ skillRecalcSubTotal }}
                    </div>
                    <div
                      v-if="skillRecalcMatchTotal > 0"
                      class="skill-recalc-match-line mb-2"
                    >
                      Unique matches (replay):
                      <strong>{{ skillRecalcMatchCurrent }}</strong>
                      of
                      <strong>{{ skillRecalcMatchTotal }}</strong>
                      <span class="skill-recalc-muted">
                        (oldest to newest)</span
                      >
                    </div>
                    <div class="d-flex align-center mb-2">
                      <span class="skill-recalc-headline mr-2">Overall</span>
                      <span class="skill-recalc-value"
                        >{{ skillRecalcProgressPercent }}%</span
                      >
                    </div>
                    <div class="skill-recalc-linear-wrap">
                      <v-progress-linear
                        :value="skillRecalcBarValue"
                        :indeterminate="false"
                        height="10"
                        color="warning"
                      />
                    </div>
                    <p
                      v-if="skillRecalcProgressDetail"
                      class="mb-1 mt-3 skill-recalc-detail"
                    >
                      {{ skillRecalcProgressDetail }}
                    </p>
                    <p
                      v-if="skillRecalcStatus"
                      class="mb-0 skill-recalc-status"
                    >
                      {{ skillRecalcStatus }}
                    </p>
                  </v-card-text>
                </v-card>
              </v-flex>
            </v-layout>
          </v-card-text>
        </v-card>
      </v-flex>

      <v-flex xs12 class="mt-3">
        <v-card
          v-if="leagues.length === 0 && !isLoading"
          class="empty-leagues-card"
        >
          <v-card-text class="text-center py-5">
            <p class="empty-leagues-msg">No divisions yet.</p>
            <p class="empty-leagues-hint">
              Add one using the form above (State → League → Division).
            </p>
          </v-card-text>
        </v-card>
        <template v-else>
          <div
            v-for="state in groupedStates"
            :key="state.name"
            class="state-group mt-4"
          >
            <h3 class="state-group-title">{{ state.name }}</h3>
            <div
              v-for="league in state.leagues"
              :key="state.name + '-' + league.name"
              class="league-group mb-3"
            >
              <div class="league-group-heading">
                <h4 class="league-group-title">{{ league.name }}</h4>
                <div class="league-group-home-visibility">
                  <v-btn
                    small
                    outlined
                    :disabled="isLoading"
                    class="mr-2"
                    @click="
                      setHideFromHomeForLeagueGroup(state.name, league.name, true)
                    "
                  >
                    <v-icon left small>visibility_off</v-icon>
                    Hide all on home
                  </v-btn>
                  <v-btn
                    small
                    outlined
                    :disabled="isLoading"
                    @click="
                      setHideFromHomeForLeagueGroup(state.name, league.name, false)
                    "
                  >
                    <v-icon left small>visibility</v-icon>
                    Show all on home
                  </v-btn>
                </div>
              </div>
              <v-expansion-panel
                :value="getExpanded(state, league)"
                popout
                focusable
                @input="setExpanded(state, league, $event)"
              >
                <v-expansion-panel-content
                  v-for="division in league.divisions"
                  :key="division.id"
                  :value="division.id"
                >
                  <template v-slot:header>
                    <div class="league-header">
                      <v-avatar size="36" class="mr-2">
                        <img
                          :src="division.data.Logo || defaultLeagueLogo"
                          :alt="division.data.Name"
                          @error="onLeagueLogoError"
                        />
                      </v-avatar>
                      <div class="title flex">{{ division.data.Name }}</div>
                      <v-chip
                        v-if="isLeagueHiddenFromHome(division.data)"
                        small
                        outline
                        color="orange"
                        class="ml-2"
                        >Hidden on home</v-chip
                      >
                      <v-chip
                        v-if="
                          division.weightclasses &&
                          division.weightclasses.length
                        "
                        small
                        class="ml-2"
                        >{{ division.weightclasses.length }} weights</v-chip
                      >
                    </div>
                  </template>
                  <v-card flat>
                    <v-card-text>
                      <v-layout row wrap>
                        <v-flex xs12 md3>
                          <v-text-field
                            v-model="division.data.State"
                            label="State"
                            placeholder="e.g. Illinois"
                          />
                        </v-flex>
                        <v-flex xs12 md3>
                          <v-text-field
                            v-model="division.data.League"
                            label="League"
                            placeholder="e.g. IESA"
                          />
                        </v-flex>
                        <v-flex xs12 md3>
                          <v-text-field
                            v-model="division.data.Division"
                            label="Division"
                            placeholder="e.g. A, AA or Bantam, Intermediate"
                            persistent-hint
                          />
                        </v-flex>
                        <v-flex xs12 md3>
                          <v-text-field
                            v-model="division.data.Name"
                            label="Display name"
                            hint="Shown in rankings (e.g. IESA A)"
                            persistent-hint
                          />
                        </v-flex>
                      </v-layout>
                      <v-layout row wrap>
                        <v-flex xs12>
                          <v-btn
                            small
                            outlined
                            :loading="division._hidePersisting"
                            :disabled="isLoading"
                            class="mt-1 mb-0"
                            @click="toggleDivisionHideFromHome(division)"
                          >
                            <v-icon left small>{{
                              isLeagueHiddenFromHome(division.data)
                                ? "visibility"
                                : "visibility_off"
                            }}</v-icon>
                            {{
                              isLeagueHiddenFromHome(division.data)
                                ? "Show on home screen"
                                : "Hide from home screen"
                            }}
                          </v-btn>
                          <p class="caption grey--text mt-2 mb-0">
                            Hidden divisions stay in League Management but have no
                            card on the home page.
                          </p>
                        </v-flex>
                      </v-layout>
                      <v-layout row wrap>
                        <v-flex xs12>
                          <v-textarea
                            v-model="division.teamSchoolsText"
                            label="Team schools (for team rankings)"
                            hint="Optional. One school per line, spelling must match wrestler School exactly. Uses one indexed read per school per weight instead of loading every athlete at each weight."
                            rows="3"
                            auto-grow
                            outlined
                            class="mt-2"
                          />
                        </v-flex>
                      </v-layout>
                      <v-layout row wrap>
                        <v-flex xs12 md6>
                          <div class="d-flex align-center">
                            <file-select
                              v-model="division._logoFile"
                              class="mr-3"
                              @input="onDivisionLogoSelected(division, $event)"
                            />
                          </div>
                          <div class="mt-2">
                            <img
                              :src="division.data.Logo || defaultLeagueLogo"
                              alt="logo"
                              style="max-height: 60px"
                              @error="onLeagueLogoError"
                            />
                          </div>
                        </v-flex>
                      </v-layout>

                      <v-layout row wrap>
                        <v-flex xs12>
                          <div class="subtitle-1 mt-3 mb-2">Weight classes</div>
                          <div class="weight-actions mb-2">
                            <v-btn
                              small
                              class="mr-2"
                              @click="addWeightclass(division)"
                            >
                              <v-icon left>add</v-icon>
                              Add weight
                            </v-btn>
                            <v-btn
                              small
                              outlined
                              class="mr-2"
                              @click="exportWeightsCsv(division)"
                            >
                              <v-icon left>file_download</v-icon>
                              Export CSV
                            </v-btn>
                            <v-btn
                              small
                              outlined
                              class="mr-2"
                              @click="exportWeightsJson(division)"
                            >
                              <v-icon left>file_download</v-icon>
                              Export JSON
                            </v-btn>
                            <v-btn
                              small
                              outlined
                              @click="triggerImportWeights(division)"
                            >
                              <v-icon left>file_upload</v-icon>
                              Import
                            </v-btn>
                            <input
                              :ref="'importFile-' + division.id"
                              type="file"
                              accept=".csv,.json,application/json"
                              class="d-none"
                              @change="onImportFile($event, division)"
                            />
                          </div>
                          <v-card flat>
                            <v-data-table
                              :key="'weights-' + division.id"
                              :headers="weightHeaders"
                              :items="division.weightclasses"
                              item-key="id"
                              :items-per-page="50"
                              class="elevation-1"
                            >
                              <template slot="items" slot-scope="props">
                                <td>
                                  <v-text-field
                                    v-model="props.item.Weight"
                                    hide-details
                                    single-line
                                    placeholder="e.g. 106"
                                    @keyup.enter="
                                      saveWeightclass(division, props.item)
                                    "
                                  />
                                </td>
                                <td>
                                  <v-text-field
                                    v-model.number="props.item.Sort"
                                    type="number"
                                    hide-details
                                    single-line
                                    @keyup.enter="
                                      saveWeightclass(division, props.item)
                                    "
                                  />
                                </td>
                                <td class="actions-cell">
                                  <span
                                    v-if="
                                      isWeightJustSaved(division, props.item)
                                    "
                                    class="saved-badge"
                                  >
                                    <v-icon small color="success"
                                      >check_circle</v-icon
                                    >
                                    Saved
                                  </span>
                                  <v-btn
                                    v-else
                                    icon
                                    small
                                    @click="
                                      saveWeightclass(division, props.item)
                                    "
                                    ><v-icon color="green">save</v-icon></v-btn
                                  >
                                  <v-btn
                                    icon
                                    small
                                    @click="
                                      deleteWeightclass(division, props.item)
                                    "
                                    ><v-icon color="red">delete</v-icon></v-btn
                                  >
                                </td>
                              </template>
                              <template slot="no-data">
                                <div class="weight-empty-state">
                                  <p>No weight classes.</p>
                                  <v-btn
                                    small
                                    color="primary"
                                    @click="addWeightclass(division)"
                                  >
                                    <v-icon left small>add</v-icon>
                                    Add weight
                                  </v-btn>
                                </div>
                              </template>
                            </v-data-table>
                          </v-card>
                          <div class="subtitle-1 mt-3 mb-1">
                            IKWF-History.org (CSV)
                          </div>
                          <p class="caption mb-2 grey--text text--darken-1">
                            Column 5 (Division) must match this division’s
                            <strong>Division</strong> field. Use
                            <strong>Import teams</strong> first, then
                            <strong>Import wrestlers</strong>, then
                            <strong>Import matches</strong>. Match import uses
                            columns 7 and 14 (lbs) with this division’s weight
                            classes (smallest class limit ≥ scale weight). Pin →
                            Fall. Each match is written to
                            <code>matches_canonical</code> only (one doc per bout,
                            same id as the server migration). After a successful import, Rankster downloads
                            an updated CSV with column 22 (<strong
                              >RanksterImported</strong
                            >
                            ISO timestamp); use that file next time so those
                            rows are skipped (saves Firestore reads and writes).
                            Other text in column 22 is ignored.
                          </p>
                          <div class="ikwf-import-actions mb-2">
                            <v-btn
                              small
                              outlined
                              class="mr-2 mb-1"
                              :loading="division._ikwfTeamsLoading"
                              @click="startIkwfHistoryImport(division, 'teams')"
                            >
                              <v-icon left small>groups</v-icon>
                              Import teams
                            </v-btn>
                            <v-btn
                              small
                              outlined
                              class="mr-2 mb-1"
                              :loading="division._ikwfWrestlersLoading"
                              @click="
                                startIkwfHistoryImport(division, 'wrestlers')
                              "
                            >
                              <v-icon left small>person_add</v-icon>
                              Import wrestlers
                            </v-btn>
                            <v-btn
                              small
                              outlined
                              class="mb-1"
                              :loading="division._ikwfMatchesLoading"
                              @click="
                                startIkwfHistoryImport(division, 'matches')
                              "
                            >
                              <v-icon left small>sports_kabaddi</v-icon>
                              Import matches
                            </v-btn>
                          </div>
                          <v-card
                            v-if="division._ikwfMatchImportSummary"
                            flat
                            class="mt-2 pa-2 ikwf-match-import-summary"
                          >
                            <div
                              class="caption grey--text text--darken-2 mb-1 font-weight-medium"
                            >
                              Last match import (remains until dismissed)
                            </div>
                            <div
                              class="body-1 ikwf-match-import-summary-text"
                              style="
                                white-space: pre-wrap;
                                word-break: break-word;
                              "
                            >
                              {{ division._ikwfMatchImportSummary }}
                            </div>
                            <v-btn
                              small
                              flat
                              class="mt-2 ma-0"
                              @click="clearIkwfMatchImportSummary(division)"
                            >
                              Dismiss
                            </v-btn>
                          </v-card>
                        </v-flex>
                      </v-layout>
                    </v-card-text>

                    <v-card-actions>
                      <v-btn
                        small
                        color="error"
                        outlined
                        :loading="division._deleteAllLoading"
                        @click="deleteDivisionWrestlersAndMatches(division)"
                      >
                        <v-icon left>delete_sweep</v-icon>
                        Delete wrestlers & matches
                      </v-btn>
                      <v-spacer></v-spacer>
                      <v-btn color="primary" @click="saveLeague(division)">
                        <v-icon left>save</v-icon>
                        Save division
                      </v-btn>
                      <v-btn
                        color="error"
                        outlined
                        @click="deleteLeague(division)"
                      >
                        <v-icon left>delete</v-icon>
                        Delete division
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </div>
          </div>
        </template>
      </v-flex>
    </v-layout>
    <navigation-menu />
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="snackbar.timeout != null ? snackbar.timeout : 3000"
      top
      multi-line
    >
      {{ snackbar.text }}
      <v-btn dark text @click="snackbar.show = false"> Close </v-btn>
    </v-snackbar>
    <input
      ref="ikwfHistoryCsvInput"
      type="file"
      accept=".csv,text/csv"
      class="d-none"
      @change="onIkwfHistoryCsvFileSelected"
    />
  </v-container>
</template>

<script>
import FileSelect from "../components/FileSelect.vue";
import NavigationMenu from "../components/NavigationMenu.vue";
import Elo from "elo-js";
import {
  getFirestore,
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  setDoc,
  serverTimestamp,
  where,
  writeBatch,
} from "firebase/firestore";
import { invalidateGlobalMaxSkillCache } from "../utils/skillBarLinear";
import { deleteAuditLogsForEntityIds } from "../utils/auditLog";
import {
  parseIkwfHistoryCsv,
  extractIkwfRow,
  divisionCsvMatchesField,
  rowHasBothWrestlersAndTeams,
  mapIkwfResultToMatchType,
  splitFullName,
  athleteDedupeKey,
  parseIkwfDate,
  commitIkwfMatch,
  normalizeKeyPart,
  parseIkwfPounds,
  pickRankWeightClassForPounds,
  isIkwfRowMarkedImported,
  buildIkwfHistoryCsvWithImportMarkers,
} from "../utils/ikwfHistoryCsvImport";
import { getApp } from "firebase/app";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  MATCHES_CANONICAL_COLLECTION,
  fetchAllCanonicalMatchDocsChronological,
} from "../utils/canonicalMatches";
import { isLeagueHiddenFromHome } from "../utils/leagueVisibility";

export default {
  name: "LeagueManager",
  components: { FileSelect, NavigationMenu },
  data: () => ({
    defaultLeagueLogo: require("../assets/logo.svg"),
    leagues: [],
    expandedPanels: [],
    expandedLeaguePanels: {},
    newLeagueState: "",
    newLeagueLeague: "",
    newLeagueDivision: "",
    newLeagueName: "",
    newLeagueLogoFile: null,
    weightHeaders: [
      { text: "Weight", value: "Weight", sortable: false },
      { text: "Sort", value: "Sort", sortable: false },
      { text: "Actions", value: "actions", sortable: false },
    ],
    isLoading: false,
    snackbar: { show: false, text: "", color: "success" },
    savedWeight: null,
    globalSkillRecalcLoading: false,
    skillRecalcProgressPercent: 0,
    skillRecalcProgressDetail: "",
    skillRecalcStatus: "",
    skillRecalcLastError: "",
    skillRecalcMatchCurrent: 0,
    skillRecalcMatchTotal: 0,
    skillRecalcStepLabel: "",
    skillRecalcSubCaption: "",
    skillRecalcSubCurrent: 0,
    skillRecalcSubTotal: 0,
    teamRankingsCacheLoading: false,
    ikwfHistoryImportPending: null,
  }),
  computed: {
    skillRecalcBarValue() {
      const v = Number(this.skillRecalcProgressPercent) || 0;
      if (this.globalSkillRecalcLoading && v === 0) return 1;
      return Math.min(100, Math.max(0, v));
    },
    groupedStates() {
      const grouped = {};
      (this.leagues || []).forEach((league) => {
        const state =
          (league.data && league.data.State) != null &&
          String(league.data.State).trim() !== ""
            ? String(league.data.State).trim()
            : "Ungrouped";
        const leagueName =
          (league.data && league.data.League) != null &&
          String(league.data.League).trim() !== ""
            ? String(league.data.League).trim()
            : "Other";
        if (!grouped[state]) grouped[state] = {};
        if (!grouped[state][leagueName]) grouped[state][leagueName] = [];
        grouped[state][leagueName].push(league);
      });
      return Object.keys(grouped)
        .sort((a, b) =>
          a === "Ungrouped" ? 1 : b === "Ungrouped" ? -1 : a.localeCompare(b)
        )
        .map((name) => ({
          name,
          leagues: Object.keys(grouped[name])
            .sort((a, b) =>
              a === "Other" ? 1 : b === "Other" ? -1 : a.localeCompare(b)
            )
            .map((leagueName) => ({
              name: leagueName,
              divisions: grouped[name][leagueName],
            })),
        }));
    },
    canCreateDivision() {
      const state = (this.newLeagueState || "").trim();
      const league = (this.newLeagueLeague || "").trim();
      const division = (this.newLeagueDivision || "").trim();
      const name = (this.newLeagueName || "").trim();
      return (state && league && division) || name.length > 0;
    },
  },
  created() {
    this.reload();
  },
  methods: {
    isLeagueHiddenFromHome,
    panelKey(state, league) {
      return (
        (state && state.name ? state.name : "") +
        "\t" +
        (league && league.name ? league.name : "")
      );
    },
    getExpanded(state, league) {
      return this.expandedLeaguePanels[this.panelKey(state, league)];
    },
    setExpanded(state, league, val) {
      this.$set(this.expandedLeaguePanels, this.panelKey(state, league), val);
    },
    onLeagueLogoError(e) {
      if (e && e.target && this.defaultLeagueLogo)
        e.target.src = this.defaultLeagueLogo;
    },
    async onDivisionLogoSelected(division, file) {
      // v-model already sets division._logoFile, but we also receive the file explicitly
      if (file) {
        this.$set(division, "_logoFile", file);
      }
      await this.uploadLogo(division);
    },
    async deleteDivisionWrestlersAndMatches(division) {
      const leagueName =
        division && division.data && division.data.Name
          ? String(division.data.Name).trim()
          : "";
      if (!leagueName) {
        this.showMsg(
          "Please set and save a display name for this division before deleting wrestlers.",
          "error"
        );
        return;
      }

      const promptText = window.prompt(
        `This will permanently delete ALL wrestlers and their matches in "${leagueName}".\n\nType "Delete" (case-sensitive) to confirm.`
      );
      if (promptText !== "Delete") {
        this.showMsg("Deletion cancelled.", "info");
        return;
      }

      const db = getFirestore();
      this.$set(division, "_deleteAllLoading", true);
      this.skillRecalcStatus = `Deleting wrestlers and matches in "${leagueName}"...`;

      try {
        // Find all wrestlers in this division
        const athletesRef = collection(db, "athletes");
        const snap = await getDocs(
          query(athletesRef, where("RankLeague", "==", leagueName))
        );

        if (snap.empty) {
          this.showMsg(
            `No wrestlers found for "${leagueName}". Nothing to delete.`,
            "info"
          );
          this.skillRecalcStatus = "";
          return;
        }

        let deletedWrestlers = 0;
        let deletedMatches = 0;
        const wrestlerIds = [];
        const matchIds = [];
        const wrestlerIdSet = new Set();

        for (const docSnap of snap.docs) {
          const wrestlerId = docSnap.id;
          wrestlerIds.push(wrestlerId);
          wrestlerIdSet.add(wrestlerId);
        }

        const canonicalToDelete = new Set();
        for (const wid of wrestlerIds) {
          const mSnap = await getDocs(
            query(
              collection(db, MATCHES_CANONICAL_COLLECTION),
              where("ParticipantAthleteIds", "array-contains", wid)
            )
          );
          mSnap.forEach((d) => {
            const x = d.data() || {};
            if (
              wrestlerIdSet.has(x.WinnerAthleteId) &&
              wrestlerIdSet.has(x.LoserAthleteId)
            ) {
              canonicalToDelete.add(d.id);
            }
          });
        }
        for (const cid of canonicalToDelete) {
          matchIds.push(cid);
          await deleteDoc(doc(db, MATCHES_CANONICAL_COLLECTION, cid));
          deletedMatches++;
        }

        for (const docSnap of snap.docs) {
          const wrestlerId = docSnap.id;

          // Delete the wrestler document itself
          await deleteDoc(doc(db, "athletes", wrestlerId));
          deletedWrestlers++;

          if (deletedWrestlers % 25 === 0) {
            this.skillRecalcStatus = `Deleted ${deletedWrestlers} wrestlers and ${deletedMatches} matches in "${leagueName}"...`;
          }
        }

        let deletedAuditRows = 0;
        this.skillRecalcStatus = "";
        try {
          deletedAuditRows += await deleteAuditLogsForEntityIds(
            db,
            "wrestler",
            wrestlerIds
          );
          deletedAuditRows += await deleteAuditLogsForEntityIds(
            db,
            "match",
            [...new Set(matchIds)]
          );
        } catch (auditErr) {
          this.showMsg(
            `Deleted ${deletedWrestlers} wrestlers and ${deletedMatches} from "${leagueName}", but audit log cleanup failed. Deploy Firestore indexes for auditLogs (entityType + entityId), then try again. ${
              auditErr.message || ""
            }`,
            "warning"
          );
          return;
        }

        const auditPart =
          deletedAuditRows > 0
            ? ` Removed ${deletedAuditRows} related audit log entries.`
            : "";
        this.showMsg(
          `Deleted ${deletedWrestlers} wrestlers and ${deletedMatches} matches from "${leagueName}".${auditPart}`,
          "success"
        );
      } catch (e) {
        this.showMsg(
          "Error deleting wrestlers and matches: " + e.message,
          "error"
        );
        this.skillRecalcStatus = "";
      } finally {
        this.$set(division, "_deleteAllLoading", false);
      }
    },
    isPanelOpen(index) {
      const v = this.expandedPanels;
      return Array.isArray(v) ? v.includes(index) : v === index;
    },
    isWeightJustSaved(league, item) {
      if (!this.savedWeight) return false;
      if (this.savedWeight.leagueId !== league.id) return false;
      const key = item._key || item.id;
      return key && this.savedWeight.itemKey === key;
    },
    showMsg(text, color = "success", timeout = 3000) {
      this.snackbar = {
        show: true,
        text,
        color,
        timeout: timeout != null ? timeout : 3000,
      };
    },
    async rebuildAllTeamRankingsCache() {
      this.teamRankingsCacheLoading = true;
      try {
        const fn = httpsCallable(
          getFunctions(getApp(), "us-central1"),
          "rebuildLeagueTeamRankingsCacheCallable",
          { timeout: 330000 }
        );
        const res = await fn({});
        const n =
          res.data &&
          res.data.all &&
          typeof res.data.all.count === "number"
            ? res.data.all.count
            : null;
        this.showMsg(
          n != null
            ? `Team rankings cache rebuilt for ${n} league(s).`
            : "Team rankings cache rebuild finished.",
          "success",
          5000
        );
      } catch (e) {
        const msg =
          e && e.message
            ? String(e.message)
            : "Team rankings cache rebuild failed.";
        this.showMsg(msg, "error", 8000);
      } finally {
        this.teamRankingsCacheLoading = false;
      }
    },
    clearIkwfMatchImportSummary(division) {
      if (division) this.$set(division, "_ikwfMatchImportSummary", "");
    },
    async reload() {
      this.isLoading = true;
      const db = getFirestore();
      try {
        const q = query(collection(db, "leagues"), orderBy("Name"));
        const snap = await getDocs(q);
        const leagues = [];
        for (const d of snap.docs) {
          const weightclasses = await this.getWeightclasses(d.id);
          const docData = d.data();
          // Always set an array reactively
          leagues.push({
            id: d.id,
            data: docData,
            teamSchoolsText: Array.isArray(docData.TeamSchools)
              ? docData.TeamSchools.join("\n")
              : "",
            weightclasses: Array.isArray(weightclasses) ? weightclasses : [],
          });
        }
        this.leagues = leagues;
      } catch (e) {
        this.showMsg("Error loading leagues: " + e.message, "error");
      } finally {
        this.isLoading = false;
      }
    },
    async getWeightclasses(leagueId) {
      const db = getFirestore();
      // Mirror home screen: leagues/{id}/weightclasses ordered by Sort then Weight
      const q = query(
        collection(db, "leagues", leagueId, "weightclasses"),
        orderBy("Sort"),
        orderBy("Weight")
      );
      const snap = await getDocs(q);
      // Normalize docs to ensure required fields exist and are of correct type
      return snap.docs.map((docSnap) => {
        const d = docSnap.data() || {};
        // Read actual DB fields (support both Weight/weight, Label/label, Sort/sort)
        const rawWeight = d.Weight !== undefined ? d.Weight : d.weight;
        const rawSort = d.Sort !== undefined ? d.Sort : d.sort;
        const rawLabel = d.Label !== undefined ? d.Label : d.label;
        // Weight is stored as a string in the DB – show exactly what’s there
        const Weight =
          rawWeight != null && rawWeight !== "" ? String(rawWeight) : "";
        const Sort =
          typeof rawSort === "number" ? rawSort : Number(rawSort) || 0;
        const Label =
          typeof rawLabel === "string"
            ? rawLabel
            : rawLabel != null
            ? String(rawLabel)
            : "";
        return { _key: docSnap.id, id: docSnap.id, Weight, Sort, Label };
      });
    },
    async createLeague() {
      const state = (this.newLeagueState || "").trim();
      const league = (this.newLeagueLeague || "").trim();
      const division = (this.newLeagueDivision || "").trim();
      let name = (this.newLeagueName || "").trim();
      if (!name && state && league && division) {
        name = league + " " + division;
      }
      if (!name) return;
      this.isLoading = true;
      const db = getFirestore();
      try {
        const payload = {
          Name: name,
          Logo: null,
          State: state || null,
          League: league || null,
          Division: division || null,
        };
        const leagueRef = await addDoc(collection(db, "leagues"), payload);
        if (this.newLeagueLogoFile) {
          const url = await this.uploadFileToStorage(
            this.newLeagueLogoFile,
            `leagues/${leagueRef.id}/logo`
          );
          await updateDoc(leagueRef, { Logo: url });
        }
        this.newLeagueState = "";
        this.newLeagueLeague = "";
        this.newLeagueDivision = "";
        this.newLeagueName = "";
        this.newLeagueLogoFile = null;
        this.showMsg("Division created successfully");
        await this.reload();
      } catch (e) {
        this.showMsg("Error creating division: " + e.message, "error");
        this.isLoading = false;
      }
    },
    async saveLeague(league) {
      this.isLoading = true;
      const db = getFirestore();
      try {
        const leagueDoc = doc(db, "leagues", league.id);
        const teamSchoolsLines = (league.teamSchoolsText || "")
          .split(/\r?\n/)
          .map((s) => s.trim())
          .filter(Boolean);
        await updateDoc(leagueDoc, {
          Name: league.data.Name,
          Logo: league.data.Logo || null,
          State:
            league.data.State != null && String(league.data.State).trim() !== ""
              ? String(league.data.State).trim()
              : null,
          League:
            league.data.League != null &&
            String(league.data.League).trim() !== ""
              ? String(league.data.League).trim()
              : null,
          Division:
            league.data.Division != null &&
            String(league.data.Division).trim() !== ""
              ? String(league.data.Division).trim()
              : null,
          TeamSchools: teamSchoolsLines.length ? teamSchoolsLines : null,
          HideFromHome: isLeagueHiddenFromHome(league.data),
        });
        if (league._logoFile) {
          const url = await this.uploadFileToStorage(
            league._logoFile,
            `leagues/${league.id}/logo`
          );
          await updateDoc(leagueDoc, { Logo: url });
          league.data.Logo = url;
          league._logoFile = null;
        }
        this.showMsg("League saved");
      } catch (e) {
        this.showMsg("Error saving league: " + e.message, "error");
      } finally {
        this.isLoading = false;
      }
    },
    async persistDivisionHideFromHome(division, hide) {
      if (!division || !division.id) return;
      this.$set(division, "_hidePersisting", true);
      const db = getFirestore();
      try {
        await updateDoc(doc(db, "leagues", division.id), {
          HideFromHome: hide === true,
        });
        this.$set(division.data, "HideFromHome", hide === true);
        this.showMsg(
          hide
            ? `"${
                division.data.Name || "Division"
              }" hidden from the home screen`
            : `"${
                division.data.Name || "Division"
              }" will show on the home screen`
        );
      } catch (e) {
        this.showMsg(
          e && e.message ? String(e.message) : "Could not update visibility",
          "error"
        );
      } finally {
        this.$set(division, "_hidePersisting", false);
      }
    },
    async toggleDivisionHideFromHome(division) {
      const next = !isLeagueHiddenFromHome(division.data);
      await this.persistDivisionHideFromHome(division, next);
    },
    async setHideFromHomeForLeagueGroup(stateName, leagueName, hide) {
      const sn = String(stateName).trim().toLowerCase();
      const ln = String(leagueName).trim().toLowerCase();
      const matches = (this.leagues || []).filter((l) => {
        const s =
          l.data && l.data.State != null && String(l.data.State).trim() !== ""
            ? String(l.data.State).trim()
            : "Ungrouped";
        const n =
          l.data && l.data.League != null && String(l.data.League).trim() !== ""
            ? String(l.data.League).trim()
            : "Other";
        return s.toLowerCase() === sn && n.toLowerCase() === ln;
      });
      if (matches.length === 0) return;
      this.isLoading = true;
      const db = getFirestore();
      try {
        for (const div of matches) {
          await updateDoc(doc(db, "leagues", div.id), {
            HideFromHome: hide === true,
          });
          this.$set(div.data, "HideFromHome", hide === true);
        }
        this.showMsg(
          hide
            ? `Hid ${matches.length} division(s) from the home screen.`
            : `Showing ${matches.length} division(s) on the home screen again.`
        );
      } catch (e) {
        this.showMsg(
          e && e.message ? String(e.message) : "Bulk update failed",
          "error"
        );
      } finally {
        this.isLoading = false;
      }
    },
    async deleteLeague(league) {
      if (
        !confirm(
          `Delete division "${league.data.Name}" and all its weight classes?`
        )
      )
        return;
      this.isLoading = true;
      const db = getFirestore();
      try {
        // Delete weights first
        for (const wc of league.weightclasses || []) {
          try {
            await deleteDoc(
              doc(db, "leagues", league.id, "weightclasses", wc.id)
            );
          } catch (_e) {
            // Weight doc may already be deleted; continue
          }
        }
        await deleteDoc(doc(db, "leagues", league.id));
        this.showMsg("League deleted");
        await this.reload();
      } catch (e) {
        this.showMsg("Error deleting league: " + e.message, "error");
        this.isLoading = false;
      }
    },
    addWeightclass(league) {
      if (!league.weightclasses) this.$set(league, "weightclasses", []);
      const _key = `k-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      league.weightclasses.push({
        _key,
        id: null,
        Weight: "",
        Label: "",
        Sort: (league.weightclasses.length + 1) * 10,
        _dirty: true,
      });
    },
    async saveWeightclass(league, item) {
      const db = getFirestore();
      try {
        if (!item.id) {
          const wcRef = await addDoc(
            collection(db, "leagues", league.id, "weightclasses"),
            {
              Weight: item.Weight != null ? String(item.Weight) : "",
              Label: item.Label || "",
              Sort: Number(item.Sort) || 0,
            }
          );
          item.id = wcRef.id;
          item._key = wcRef.id;
        } else {
          await updateDoc(
            doc(db, "leagues", league.id, "weightclasses", item.id),
            {
              Weight: item.Weight != null ? String(item.Weight) : "",
              Label: item.Label || "",
              Sort: Number(item.Sort) || 0,
            }
          );
        }
        this.savedWeight = {
          leagueId: league.id,
          itemKey: item._key || item.id,
        };
        setTimeout(() => {
          this.savedWeight = null;
        }, 2500);
        this.showMsg("Weight class saved");
      } catch (e) {
        this.showMsg("Error saving weight: " + e.message, "error");
      }
    },
    async deleteWeightclass(league, item) {
      const db = getFirestore();
      try {
        if (item.id)
          await deleteDoc(
            doc(db, "leagues", league.id, "weightclasses", item.id)
          );
        league.weightclasses = league.weightclasses.filter((w) => w !== item);
      } catch (e) {
        this.showMsg("Error deleting weight: " + e.message, "error");
      }
    },
    async uploadLogo(league) {
      if (!league._logoFile) return;
      this.isLoading = true;
      const db = getFirestore();
      try {
        const url = await this.uploadFileToStorage(
          league._logoFile,
          `leagues/${league.id}/logo`
        );
        await updateDoc(doc(db, "leagues", league.id), { Logo: url });
        league.data.Logo = url;
        league._logoFile = null;
        this.showMsg("Logo uploaded");
      } catch (e) {
        this.showMsg("Error uploading logo: " + e.message, "error");
      } finally {
        this.isLoading = false;
      }
    },
    async uploadFileToStorage(file, path) {
      const storage = getStorage();
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, file);
      return await getDownloadURL(storageRef);
    },
    exportWeightsCsv(league) {
      const rows = (league.weightclasses || []).map((w) => [
        String(w.Weight ?? ""),
        Number(w.Sort) ?? 0,
      ]);
      const header = "Weight,Sort\n";
      const body = rows.map((r) => r.join(",")).join("\n");
      this.downloadFile(
        header + body,
        `${(league.data.Name || "league").replace(
          /[^a-z0-9]/gi,
          "_"
        )}_weights.csv`,
        "text/csv"
      );
    },
    exportWeightsJson(league) {
      const arr = (league.weightclasses || []).map((w) => ({
        Weight: String(w.Weight ?? ""),
        Sort: Number(w.Sort) ?? 0,
      }));
      this.downloadFile(
        JSON.stringify(arr, null, 2),
        `${(league.data.Name || "league").replace(
          /[^a-z0-9]/gi,
          "_"
        )}_weights.json`,
        "application/json"
      );
    },
    downloadFile(content, filename, mimeType) {
      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 250);
    },
    triggerImportWeights(league) {
      const el = this.$refs["importFile-" + league.id];
      if (el && el[0]) el[0].click();
      else if (el) el.click();
    },
    startIkwfHistoryImport(division, mode) {
      const leagueName =
        division && division.data && division.data.Name
          ? String(division.data.Name).trim()
          : "";
      if (!leagueName) {
        this.showMsg(
          "Set and save a display name for this division before CSV import.",
          "error"
        );
        return;
      }
      const divField =
        division && division.data && division.data.Division != null
          ? String(division.data.Division).trim()
          : "";
      if (!divField) {
        this.showMsg(
          "Set the Division field (e.g. Bantam) so column 5 in the CSV can be matched.",
          "error"
        );
        return;
      }
      this.ikwfHistoryImportPending = { division, mode };
      this.$nextTick(() => {
        const el = this.$refs.ikwfHistoryCsvInput;
        if (el) el.click();
      });
    },
    async onIkwfHistoryCsvFileSelected(ev) {
      const input = ev.target;
      const file = input.files && input.files[0];
      input.value = "";
      const pending = this.ikwfHistoryImportPending;
      this.ikwfHistoryImportPending = null;
      if (!file || !pending) return;

      const { division, mode } = pending;
      const loadingKey =
        mode === "teams"
          ? "_ikwfTeamsLoading"
          : mode === "wrestlers"
          ? "_ikwfWrestlersLoading"
          : "_ikwfMatchesLoading";
      this.$set(division, loadingKey, true);

      try {
        const text = await file.text();
        const parsedRows = parseIkwfHistoryCsv(text);
        const divisionField = String(
          (division.data && division.data.Division) || ""
        ).trim();

        if (mode === "teams") {
          await this._ikwfImportTeams(division, parsedRows, divisionField);
        } else if (mode === "wrestlers") {
          await this._ikwfImportWrestlers(division, parsedRows, divisionField);
        } else {
          await this._ikwfImportMatches(division, parsedRows, divisionField, {
            originalText: text,
            fileName: file.name || "ikwf-history.csv",
          });
        }
      } catch (e) {
        this.showMsg(
          "IKWF-History import failed: " +
            (e && e.message ? e.message : String(e)),
          "error"
        );
      } finally {
        this.$set(division, loadingKey, false);
      }
    },
    async _ikwfImportTeams(division, parsedRows, divisionField) {
      const db = getFirestore();
      const teams = new Set();
      for (const pr of parsedRows) {
        const r = extractIkwfRow(pr.cells);
        if (!r || !divisionCsvMatchesField(r.division, divisionField)) continue;
        if (r.winnerTeam) teams.add(String(r.winnerTeam).trim());
        if (r.loserTeam) teams.add(String(r.loserTeam).trim());
      }
      const schoolSnap = await getDocs(
        query(collection(db, "schools"), orderBy("Name"))
      );
      const existing = new Set();
      schoolSnap.forEach((d) => {
        const n = d.data().Name;
        if (n != null && String(n).trim()) existing.add(normalizeKeyPart(n));
      });
      let created = 0;
      for (const rawName of teams) {
        const n = String(rawName).trim();
        if (!n) continue;
        const k = normalizeKeyPart(n);
        if (existing.has(k)) continue;
        await addDoc(collection(db, "schools"), { Name: n });
        existing.add(k);
        created++;
      }
      this.showMsg(
        `IKWF-History.org — Teams: added ${created} new school(s). ${teams.size} unique team name(s) in matching rows.`
      );
    },
    async _ikwfImportWrestlers(division, parsedRows, divisionField) {
      const db = getFirestore();
      const rankLeague = String(
        (division.data && division.data.Name) || ""
      ).trim();
      const studs = new Map();

      for (const pr of parsedRows) {
        const r = extractIkwfRow(pr.cells);
        if (!r || !divisionCsvMatchesField(r.division, divisionField)) continue;
        if (!rowHasBothWrestlersAndTeams(r)) continue;

        const wk = athleteDedupeKey(r.winnerName, r.winnerTeam, rankLeague);
        if (!studs.has(wk)) {
          studs.set(wk, {
            fullName: String(r.winnerName).trim(),
            school: String(r.winnerTeam).trim(),
            grade: String(r.winnerGrade || "").trim(),
          });
        }
        const lk = athleteDedupeKey(r.loserName, r.loserTeam, rankLeague);
        if (!studs.has(lk)) {
          studs.set(lk, {
            fullName: String(r.loserName).trim(),
            school: String(r.loserTeam).trim(),
            grade: String(r.loserGrade || "").trim(),
          });
        }
      }

      const athletesSnap = await getDocs(
        query(collection(db, "athletes"), where("RankLeague", "==", rankLeague))
      );
      const existing = new Set();
      athletesSnap.forEach((d) => {
        const D = d.data();
        const fn =
          (D.FullName && String(D.FullName).trim()) ||
          `${D.FirstName || ""} ${D.LastName || ""}`.trim();
        existing.add(athleteDedupeKey(fn, D.School, rankLeague));
      });

      let created = 0;
      for (const [, info] of studs) {
        const k = athleteDedupeKey(info.fullName, info.school, rankLeague);
        if (existing.has(k)) continue;
        const { firstName, lastName } = splitFullName(info.fullName);
        await addDoc(collection(db, "athletes"), {
          FirstName: firstName,
          LastName: lastName,
          FullName: info.fullName,
          RankWeightClass: "",
          RankLeague: rankLeague,
          School: info.school,
          Grade: info.grade || "",
          Image: "",
          Skill: 1200,
          Wins: 0,
          Losses: 0,
        });
        existing.add(k);
        created++;
      }
      this.showMsg(
        `IKWF-History.org — Wrestlers: created ${created}. ${studs.size} unique wrestler(s) in matching rows.`
      );
    },
    async _ikwfImportMatches(division, parsedRows, divisionField, fileCtx) {
      const db = getFirestore();
      const rankLeague = String(
        (division.data && division.data.Name) || ""
      ).trim();

      let skippedMarked = 0;
      const candidates = [];
      for (const pr of parsedRows) {
        const r = extractIkwfRow(pr.cells);
        if (!r || !divisionCsvMatchesField(r.division, divisionField)) continue;
        if (!rowHasBothWrestlersAndTeams(r)) continue;
        if (isIkwfRowMarkedImported(pr.cells)) {
          skippedMarked++;
          continue;
        }
        const d = parseIkwfDate(r.dateRaw);
        if (!d) continue;
        if (!String(r.resultRaw || "").trim()) continue;
        const type = mapIkwfResultToMatchType(r.resultRaw);
        candidates.push({
          r,
          matchDate: d,
          type,
          lineIndex: pr.lineIndex || 0,
        });
      }
      // Oldest → newest event date so each Elo step uses prior bouts’ skills.
      // Same timestamp: preserve CSV row order (stable tie-break).
      candidates.sort((a, b) => {
        const dt = a.matchDate - b.matchDate;
        if (dt !== 0) return dt;
        return a.lineIndex - b.lineIndex;
      });

      const athletesSnap = await getDocs(
        query(collection(db, "athletes"), where("RankLeague", "==", rankLeague))
      );
      const idByKey = new Map();
      athletesSnap.forEach((d) => {
        const D = d.data();
        const fn =
          (D.FullName && String(D.FullName).trim()) ||
          `${D.FirstName || ""} ${D.LastName || ""}`.trim();
        const key = athleteDedupeKey(fn, D.School, rankLeague);
        idByKey.set(key, d.id);
      });

      let written = 0;
      let dup = 0;
      let missing = 0;
      let errors = 0;
      let stoppedByQuota = false;
      let quotaErrorDetail = "";
      const sessionDupCache = new Set();
      const importedLineNumbers = new Set();

      let processedCandidateIndex = 0;
      for (const c of candidates) {
        processedCandidateIndex += 1;
        const { r, matchDate, type } = c;
        const wk = athleteDedupeKey(r.winnerName, r.winnerTeam, rankLeague);
        const lk = athleteDedupeKey(r.loserName, r.loserTeam, rankLeague);
        const wid = idByKey.get(wk);
        const lid = idByKey.get(lk);
        if (!wid || !lid) {
          missing++;
          continue;
        }
        const wLb = parseIkwfPounds(r.winnerWeightRaw);
        const lLb = parseIkwfPounds(r.loserWeightRaw);
        const winnerRankWeightClass = pickRankWeightClassForPounds(
          division.weightclasses,
          wLb
        );
        const loserRankWeightClass = pickRankWeightClassForPounds(
          division.weightclasses,
          lLb
        );
        const res = await commitIkwfMatch(db, {
          winnerId: wid,
          loserId: lid,
          eventName: r.event,
          matchDate,
          matchType: type,
          winnerRankWeightClass: winnerRankWeightClass || undefined,
          loserRankWeightClass: loserRankWeightClass || undefined,
          sessionDupCache,
        });
        if (res.ok) {
          written++;
          if (c.lineIndex) importedLineNumbers.add(c.lineIndex);
        } else if (res.skip === "duplicate") {
          dup++;
          // Mark row so the next import skips Firestore duplicate checks entirely.
          if (c.lineIndex) importedLineNumbers.add(c.lineIndex);
        } else if (res.skip === "missing_athlete") {
          missing++;
        } else if (res.quotaExceeded) {
          stoppedByQuota = true;
          quotaErrorDetail = (res.error && String(res.error).trim()) || "";
          break;
        } else {
          errors++;
        }
      }

      const notAttemptedAfterQuota =
        stoppedByQuota && processedCandidateIndex < candidates.length
          ? candidates.length - processedCandidateIndex
          : 0;

      let csvDownloadAttempted = false;
      if (importedLineNumbers.size > 0 && fileCtx && fileCtx.originalText) {
        const base =
          (fileCtx.fileName || "ikwf-history.csv").replace(/\.[^./\\]+$/, "") ||
          "ikwf-history";
        const csvOut = buildIkwfHistoryCsvWithImportMarkers(
          fileCtx.originalText,
          importedLineNumbers
        );
        this.downloadFile(
          csvOut,
          `${base}-rankster-imported.csv`,
          "text/csv;charset=utf-8"
        );
        csvDownloadAttempted = true;
      }

      invalidateGlobalMaxSkillCache();
      if (written > 0) this.$root.$emit("rankster-seasons-changed");

      const IKWF_MATCH_SNACK_MS = 45000;
      const lines = [
        "IKWF-History.org — Match import results",
        `Queued for processing (division + wrestlers/teams + date/result): ${candidates.length}`,
      ];
      if (skippedMarked > 0) {
        lines.push(
          `Skipped (column 22 has Rankster ISO timestamp from a prior export): ${skippedMarked}`
        );
      }
      lines.push(
        `Added: ${written}`,
        `Duplicates (already in database): ${dup}`,
        `Missing wrestler pair in this league: ${missing}`,
        `Other errors: ${errors}`
      );
      if (candidates.length === 0 && skippedMarked === 0) {
        lines.push(
          "No rows were queued. Check: CSV column 5 (Division) matches this division; Import wrestlers was run for this league name; Date and Result (column 21) are present."
        );
      }
      if (csvDownloadAttempted) {
        lines.push(
          "Your browser should save “*-rankster-imported.csv” (check the download bar / Downloads folder). Column 22 has Rankster timestamps on processed rows."
        );
      } else if (importedLineNumbers.size > 0) {
        lines.push(
          "Processed rows were tracked but CSV export was skipped (missing file text). Re-import the same file if you need an updated CSV."
        );
      }
      const summary = lines.join("\n");
      this.$set(division, "_ikwfMatchImportSummary", summary);

      const snackText = `Matches: ${written} added, ${dup} duplicates, ${missing} missing pair, ${errors} errors${
        skippedMarked ? `, ${skippedMarked} skipped (CSV)` : ""
      }.${
        stoppedByQuota
          ? " Stopped at Firestore quota — progress saved like a normal run."
          : ""
      }${
        csvDownloadAttempted
          ? " CSV download started — check your Downloads folder."
          : ""
      } Full report stays under the import buttons until dismissed.`;
      let snackColor = "success";
      if (stoppedByQuota) {
        snackColor = written + dup > 0 ? "warning" : "info";
      } else if (errors > 0) {
        snackColor = "error";
      } else if (written === 0 && candidates.length > 0) {
        snackColor = "info";
      }
      this.showMsg(snackText, snackColor, IKWF_MATCH_SNACK_MS);
    },
    async onImportFile(event, league) {
      const file = event.target.files && event.target.files[0];
      if (!file) return;
      event.target.value = "";
      const text = await file.text();
      let rows = [];
      const ext = (file.name || "").toLowerCase();
      if (ext.endsWith(".json")) {
        try {
          const parsed = JSON.parse(text);
          rows = Array.isArray(parsed)
            ? parsed
            : parsed.weightclasses || parsed.weights || [];
        } catch (e) {
          this.showMsg("Invalid JSON: " + e.message, "error");
          return;
        }
      } else {
        const lines = text.split(/\r?\n/).filter((l) => l.trim());
        const isHeader = (line) =>
          /^weight\s*,?\s*sort$/i.test(line.replace(/\s/g, ""));
        lines.forEach((line, i) => {
          if (i === 0 && isHeader(line)) return;
          const parts = line.split(",").map((p) => p.trim());
          const weight = parts[0] ?? "";
          const sort = Number(parts[1]) || (i + 1) * 10;
          rows.push({ Weight: weight, Sort: sort });
        });
      }
      const normalized = rows.map((r) => ({
        Weight: r.Weight != null ? String(r.Weight) : "",
        Sort: typeof r.Sort === "number" ? r.Sort : Number(r.Sort) || 0,
      }));
      if (normalized.length === 0) {
        this.showMsg("No weight classes found in file", "error");
        return;
      }
      this.isLoading = true;
      const db = getFirestore();
      try {
        for (const w of normalized) {
          await addDoc(collection(db, "leagues", league.id, "weightclasses"), {
            Weight: w.Weight,
            Label: "",
            Sort: w.Sort,
          });
        }
        const weightclasses = await this.getWeightclasses(league.id);
        league.weightclasses = weightclasses;
        this.showMsg(`Imported ${normalized.length} weight class(es)`);
      } catch (e) {
        this.showMsg("Import failed: " + e.message, "error");
      } finally {
        this.isLoading = false;
      }
    },

    updateSkillRecalcProgress(
      overallPercent,
      detailLine,
      statusLine,
      stepLabel
    ) {
      const p = Math.min(100, Math.max(0, Math.round(overallPercent)));
      this.skillRecalcProgressPercent = p;
      if (detailLine != null) this.skillRecalcProgressDetail = detailLine;
      if (statusLine != null) this.skillRecalcStatus = statusLine;
      if (stepLabel !== undefined && stepLabel !== null) {
        this.skillRecalcStepLabel = stepLabel;
      }
    },
    async resetAllSkillsGlobalRecalc() {
      const STEPS = 7;
      if (
        !confirm(
          "Reset EVERY wrestler in the database to 1200, then replay each unique match exactly once in date order (oldest to newest)? Wins, losses, skills, match skill-change fields, and ranking snapshots will be updated. This cannot be undone."
        )
      ) {
        return;
      }

      const db = getFirestore();
      this.globalSkillRecalcLoading = true;
      this.skillRecalcLastError = "";
      this.skillRecalcMatchCurrent = 0;
      this.skillRecalcMatchTotal = 0;
      this.skillRecalcSubCaption = "";
      this.skillRecalcSubCurrent = 0;
      this.skillRecalcSubTotal = 0;
      this.updateSkillRecalcProgress(
        1,
        "Step 1 of " + STEPS + ": Loading athlete list from Firestore...",
        "Reading /athletes...",
        "Step 1 of " + STEPS
      );

      try {
        const ST = {
          END_LOAD: 3,
          END_RESET: 18,
          END_MATCHES: 33,
          END_DEDUPE: 35,
          END_REPLAY: 88,
          END_MW: 94,
          END_ATH: 97,
          END_DONE: 100,
        };
        const pctReset = (done, total) => {
          if (!total) return ST.END_LOAD;
          return ST.END_LOAD + (ST.END_RESET - ST.END_LOAD) * (done / total);
        };
        const pctMatches = (done, total) => {
          if (!total) return ST.END_RESET;
          return (
            ST.END_RESET + (ST.END_MATCHES - ST.END_RESET) * (done / total)
          );
        };
        const pctReplay = (done, total) => {
          if (!total) return ST.END_REPLAY;
          return (
            ST.END_DEDUPE + (ST.END_REPLAY - ST.END_DEDUPE) * (done / total)
          );
        };
        const pctMatchWrites = (done, total) => {
          if (!total) return ST.END_MW;
          return ST.END_REPLAY + (ST.END_MW - ST.END_REPLAY) * (done / total);
        };
        const pctAthletes = (done, total) => {
          if (!total) return ST.END_ATH;
          return ST.END_MW + (ST.END_ATH - ST.END_MW) * (done / total);
        };
        const pctSnapshots = (done, total) => {
          if (!total) return ST.END_DONE;
          return ST.END_ATH + (ST.END_DONE - ST.END_ATH) * (done / total);
        };

        // Pace batch commits: max-size bursts back-to-back trigger Firestore throttling
        // ("maximum backoff delay" in console). Smaller batches + delay between commits.
        const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
        const FS_BATCH_MAX_OPS = 250;
        const FS_AFTER_BATCH_MS = 300;

        const athletesSnap = await getDocs(collection(db, "athletes"));
        const allIds = athletesSnap.docs.map((d) => d.id);
        const validAthleteIds = new Set(allIds);

        this.updateSkillRecalcProgress(
          ST.END_LOAD,
          "Step 1 of " +
            STEPS +
            ": Found " +
            allIds.length +
            " wrestler document(s).",
          "Ready to reset skills to 1200.",
          "Step 1 of " + STEPS
        );

        if (allIds.length === 0) {
          this.showMsg("No wrestlers in the database.", "info");
          this.updateSkillRecalcProgress(0, "", "", "");
          return;
        }

        this.skillRecalcSubCaption = "Wrestlers reset (Firestore)";
        this.skillRecalcSubTotal = allIds.length;
        this.skillRecalcSubCurrent = 0;

        this.updateSkillRecalcProgress(
          pctReset(0, allIds.length),
          "Step 2 of " +
            STEPS +
            ": Queuing " +
            allIds.length +
            " wrestler reset(s) (Skill 1200, Wins 0, Losses 0)...",
          "Commits in batches of up to " +
            FS_BATCH_MAX_OPS +
            " with a short pause after each (reduces throttling).",
          "Step 2 of " + STEPS
        );

        let batchesCommitted = 0;
        {
          let batch = writeBatch(db);
          let ops = 0;
          let done = 0;
          for (const id of allIds) {
            batch.update(doc(db, "athletes", id), {
              Skill: 1200,
              Wins: 0,
              Losses: 0,
            });
            ops++;
            done++;
            this.skillRecalcSubCurrent = done;
            if (done % 25 === 0 || done === allIds.length) {
              this.updateSkillRecalcProgress(
                pctReset(done, allIds.length),
                "Step 2 of " +
                  STEPS +
                  ": Prepared reset for " +
                  done +
                  " / " +
                  allIds.length +
                  " wrestlers (in memory queue).",
                ops >= FS_BATCH_MAX_OPS
                  ? "Committing a batch of up to " +
                      FS_BATCH_MAX_OPS +
                      " writes to Firestore..."
                  : "Adding updates to the current batch...",
                "Step 2 of " + STEPS
              );
              await this.$nextTick();
              await sleep(0);
            }
            if (ops >= FS_BATCH_MAX_OPS) {
              await batch.commit();
              batchesCommitted++;
              await sleep(FS_AFTER_BATCH_MS);
              batch = writeBatch(db);
              ops = 0;
              this.updateSkillRecalcProgress(
                pctReset(done, allIds.length),
                "Step 2 of " +
                  STEPS +
                  ": Committed Firestore batch " +
                  batchesCommitted +
                  ". " +
                  done +
                  " / " +
                  allIds.length +
                  " wrestler reset(s) written so far.",
                "Pausing briefly so Firestore can keep up...",
                "Step 2 of " + STEPS
              );
              await sleep(0);
            }
          }
          if (ops > 0) {
            await batch.commit();
            batchesCommitted++;
            await sleep(FS_AFTER_BATCH_MS);
          }
        }

        this.updateSkillRecalcProgress(
          ST.END_RESET,
          "Step 2 of " +
            STEPS +
            ": Finished resetting all " +
            allIds.length +
            " wrestler(s) (" +
            batchesCommitted +
            " Firestore batch commit(s)).",
          "Building in-memory state for replay...",
          "Step 2 of " + STEPS
        );

        const stateById = {};
        for (const id of allIds) {
          stateById[id] = { skill: 1200, wins: 0, losses: 0 };
        }

        this.skillRecalcSubCaption = "matches_canonical";
        this.skillRecalcSubTotal = 1;
        this.skillRecalcSubCurrent = 0;

        this.updateSkillRecalcProgress(
          pctMatches(0, 1),
          "Step 3 of " +
            STEPS +
            ": Loading all bouts from matches_canonical (paged)...",
          "Oldest-first order for replay.",
          "Step 3 of " + STEPS
        );

        const allCanonDocs = await fetchAllCanonicalMatchDocsChronological(db);
        this.skillRecalcSubCurrent = 1;
        this.updateSkillRecalcProgress(
          ST.END_MATCHES,
          "Step 3 of " +
            STEPS +
            ": Loaded " +
            allCanonDocs.length +
            " canonical match document(s).",
          "Filtering to known wrestlers...",
          "Step 3 of " + STEPS
        );

        const sortedBouts = allCanonDocs.filter((d) => {
          const x = d.data() || {};
          return (
            validAthleteIds.has(x.WinnerAthleteId) &&
            validAthleteIds.has(x.LoserAthleteId)
          );
        });

        const boutTotal = sortedBouts.length;
        this.skillRecalcMatchTotal = boutTotal;
        this.skillRecalcMatchCurrent = 0;
        this.skillRecalcSubTotal = 0;
        this.skillRecalcSubCurrent = 0;

        this.updateSkillRecalcProgress(
          ST.END_DEDUPE,
          boutTotal === 0
            ? "Step 4 of " +
                STEPS +
                ": No bouts to replay for current wrestlers."
            : "Step 4 of " +
                STEPS +
                ": " +
                boutTotal +
                " bout(s) to replay (already ordered oldest → newest).",
          "Source: matches_canonical.",
          "Step 4 of " + STEPS
        );

        const elo = new Elo();
        const matchSkillUpdates = [];

        for (let gi = 0; gi < sortedBouts.length; gi++) {
          const d = sortedBouts[gi];
          const match = d.data() || {};
          const winnerId = match.WinnerAthleteId;
          const loserId = match.LoserAthleteId;

          if (
            !winnerId ||
            !loserId ||
            !stateById[winnerId] ||
            !stateById[loserId]
          ) {
            this.skillRecalcMatchCurrent = gi + 1;
            this.updateSkillRecalcProgress(
              pctReplay(gi + 1, boutTotal),
              "Step 5 of " +
                STEPS +
                ": Bout " +
                (gi + 1) +
                " of " +
                boutTotal +
                " — skipped (missing IDs).",
              "",
              "Step 5 of " + STEPS
            );
            continue;
          }

          const wWinner = stateById[winnerId];
          const wLoser = stateById[loserId];
          const oldW = wWinner.skill;
          const oldL = wLoser.skill;

          let newSkillW = elo.ifWins(wWinner.skill, wLoser.skill);
          let newSkillL = elo.ifLoses(wLoser.skill, wWinner.skill);
          wWinner.wins++;
          wLoser.losses++;

          let multiFactor = 1.0;
          if (match.Type === "Major" || match.Type === "Technical Fall") {
            multiFactor = 1.2;
          } else if (match.Type === "Fall") {
            multiFactor = 1.3;
          }

          newSkillW =
            wWinner.skill + (newSkillW - wWinner.skill) * multiFactor;
          newSkillL = wLoser.skill + (newSkillL - wLoser.skill) * multiFactor;

          wWinner.skill = Math.max(1, newSkillW);
          wLoser.skill = Math.max(1, newSkillL);

          const deltaW = Math.round((wWinner.skill - oldW) * 100) / 100;
          const deltaL = Math.round((wLoser.skill - oldL) * 100) / 100;

          matchSkillUpdates.push({
            matchId: d.id,
            skillChangeWinner: deltaW,
            skillChangeLoser: deltaL,
          });

          this.skillRecalcMatchCurrent = gi + 1;
          this.updateSkillRecalcProgress(
            pctReplay(gi + 1, boutTotal),
            "Step 5 of " +
              STEPS +
              ": Replayed " +
              (gi + 1) +
              " / " +
              boutTotal +
              " bout(s).",
            "In-memory Elo only.",
            "Step 5 of " + STEPS
          );

          if (
            gi === sortedBouts.length - 1 ||
            (gi + 1) % 25 === 0 ||
            boutTotal < 40
          ) {
            await new Promise((r) => setTimeout(r, 0));
          }
        }

        const mwTotal = matchSkillUpdates.length;
        this.skillRecalcSubCaption = "matches_canonical (skill deltas)";
        this.skillRecalcSubTotal = mwTotal;
        this.skillRecalcSubCurrent = 0;

        this.updateSkillRecalcProgress(
          ST.END_REPLAY,
          "Step 6 of " +
            STEPS +
            ": Writing SkillChangeWinner / SkillChangeLoser on " +
            mwTotal +
            " bout document(s)...",
          "Batched writes with pacing.",
          "Step 6 of " + STEPS
        );

        {
          let mb = writeBatch(db);
          let mops = 0;
          let mwDone = 0;
          for (const u of matchSkillUpdates) {
            mb.update(
              doc(db, MATCHES_CANONICAL_COLLECTION, u.matchId),
              {
                SkillChangeWinner: u.skillChangeWinner,
                SkillChangeLoser: u.skillChangeLoser,
              }
            );
            mops++;
            mwDone++;
            this.skillRecalcSubCurrent = mwDone;
            if (mops >= FS_BATCH_MAX_OPS) {
              await mb.commit();
              await sleep(FS_AFTER_BATCH_MS);
              mb = writeBatch(db);
              mops = 0;
              this.updateSkillRecalcProgress(
                pctMatchWrites(mwDone, mwTotal),
                "Step 6 of " +
                  STEPS +
                  ": Saved skill deltas for " +
                  mwDone +
                  " / " +
                  mwTotal +
                  " bout(s).",
                "Pausing briefly after batch commit...",
                "Step 6 of " + STEPS
              );
              await sleep(0);
            }
          }
          if (mops > 0) {
            await mb.commit();
            await sleep(FS_AFTER_BATCH_MS);
          }
        }

        this.skillRecalcSubCaption = "Wrestlers (final Skill / W-L)";
        this.skillRecalcSubTotal = allIds.length;
        this.skillRecalcSubCurrent = 0;

        this.updateSkillRecalcProgress(
          ST.END_MW,
          "Step 7 of " +
            STEPS +
            ": Saving final Skill, Wins, and Losses for " +
            allIds.length +
            " wrestler(s)...",
          "Batched writes (not one RPC per wrestler).",
          "Step 7 of " + STEPS
        );

        {
          let ab = writeBatch(db);
          let aops = 0;
          let athWritten = 0;
          for (let wi = 0; wi < allIds.length; wi++) {
            const id = allIds[wi];
            const w = stateById[id];
            if (!w) continue;
            ab.update(doc(db, "athletes", id), {
              Skill: Math.round(w.skill * 100) / 100,
              Wins: w.wins,
              Losses: w.losses,
            });
            aops++;
            athWritten++;
            this.skillRecalcSubCurrent = athWritten;
            if (aops >= FS_BATCH_MAX_OPS) {
              await ab.commit();
              await sleep(FS_AFTER_BATCH_MS);
              ab = writeBatch(db);
              aops = 0;
            }
            if (wi % 40 === 0 || wi === allIds.length - 1) {
              this.updateSkillRecalcProgress(
                pctAthletes(athWritten, allIds.length),
                "Step 7 of " +
                  STEPS +
                  ": Queued final save for " +
                  athWritten +
                  " / " +
                  allIds.length +
                  " wrestler document(s).",
                aops > 0 ? "Committing batches..." : "Saving...",
                "Step 7 of " + STEPS
              );
              await sleep(0);
            }
          }
          if (aops > 0) {
            await ab.commit();
            await sleep(FS_AFTER_BATCH_MS);
          }
        }

        const byLeagueWeight = {};
        athletesSnap.docs.forEach((docSnap) => {
          const data = docSnap.data();
          const league =
            data.RankLeague != null ? String(data.RankLeague).trim() : "";
          const weight =
            data.RankWeightClass != null ? String(data.RankWeightClass) : "";
          if (!league) return;
          const key = `${league}\t${weight}`;
          if (!byLeagueWeight[key]) byLeagueWeight[key] = [];
          const skill = stateById[docSnap.id]
            ? stateById[docSnap.id].skill
            : data.Skill || 0;
          byLeagueWeight[key].push({ id: docSnap.id, skill });
        });

        const snapshotId = (league, weight) =>
          `${(league || "").replace(/\//g, "_")}__${(weight != null
            ? String(weight)
            : ""
          ).replace(/\//g, "_")}`;

        const snapKeys = Object.keys(byLeagueWeight);
        this.skillRecalcSubCaption = "Ranking snapshots";
        this.skillRecalcSubTotal = snapKeys.length;
        this.skillRecalcSubCurrent = 0;

        this.updateSkillRecalcProgress(
          ST.END_ATH,
          "Step 7 (continued): Writing " +
            snapKeys.length +
            " ranking snapshot(s)...",
          "By league and weight class.",
          "Step 7 of " + STEPS
        );

        for (let si = 0; si < snapKeys.length; si++) {
          const key = snapKeys[si];
          const [league, weight] = key.split("\t");
          const list = byLeagueWeight[key];
          list.sort((a, b) => b.skill - a.skill);
          const ranks = {};
          list.forEach((item, idx) => {
            ranks[item.id] = idx + 1;
          });
          await setDoc(
            doc(db, "rankingsSnapshots", snapshotId(league, weight)),
            { league, weight, ranks, updatedAt: serverTimestamp() }
          );
          this.skillRecalcSubCurrent = si + 1;
          this.updateSkillRecalcProgress(
            pctSnapshots(si + 1, snapKeys.length),
            "Step 7 of " +
              STEPS +
              ": Saved snapshot " +
              (si + 1) +
              " / " +
              snapKeys.length +
              ".",
            "",
            "Step 7 of " + STEPS
          );
        }

        invalidateGlobalMaxSkillCache();

        this.skillRecalcSubTotal = 0;
        this.skillRecalcSubCurrent = 0;

        this.updateSkillRecalcProgress(
          ST.END_DONE,
          "Done. Unique matches replayed: " +
            boutTotal +
            ". Match docs updated: " +
            matchSkillUpdates.length +
            ".",
          "All steps complete. Wrestlers: " + allIds.length + ".",
          "Finished"
        );

        this.showMsg(
          "Global skill recalc finished: " +
            boutTotal +
            " unique match(es), " +
            matchSkillUpdates.length +
            " match document update(s).",
          "success"
        );
      } catch (e) {
        const msg = e && e.message ? e.message : String(e);
        this.skillRecalcLastError = msg;
        this.updateSkillRecalcProgress(
          this.skillRecalcProgressPercent,
          this.skillRecalcProgressDetail,
          "Error: " + msg
        );
        this.showMsg("Skill recalculation failed: " + msg, "error");
      } finally {
        this.globalSkillRecalcLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.state-group-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #374151;
  margin: 0 0 0.5rem;
  padding-bottom: 0.25rem;
  border-bottom: 2px solid #e5e7eb;
}
.league-group-heading {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.35rem;
}
.league-group-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #6b7280;
  margin: 0;
  padding-left: 0.5rem;
}
.league-group-home-visibility {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
  padding-left: 0.5rem;
}
.league-header {
  display: flex;
  align-items: center;
  width: 100%;
}
.actions-cell {
  white-space: nowrap;
}
.saved-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #4caf50;
  font-size: 12px;
  margin-right: 8px;
}
.weight-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
}

.empty-leagues-card {
  border: 1px dashed rgba(0, 0, 0, 0.2);
}

.empty-leagues-msg {
  font-size: 1rem;
  color: #374151;
  margin: 0 0 4px;
}

.empty-leagues-hint {
  font-size: 0.9rem;
  color: #6b7280;
  margin: 0;
}

.weight-empty-state {
  padding: 24px 16px;
  text-align: center;
}

.weight-empty-state p {
  margin: 0 0 12px;
  color: #6b7280;
  font-size: 0.95rem;
}

.skill-recalc-progress-card {
  background: #ffffff !important;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}
.skill-recalc-headline {
  font-weight: 600;
  color: #111827;
}
.skill-recalc-value {
  font-weight: 600;
  color: #1f2937;
}
.skill-recalc-match-line {
  font-size: 1rem;
  color: #111827;
  line-height: 1.4;
}
.skill-recalc-step-label {
  font-size: 0.95rem;
  font-weight: 600;
  color: #111827;
}
.skill-recalc-sub-line {
  font-size: 0.9rem;
  color: #374151;
  line-height: 1.45;
}
.skill-recalc-linear-wrap {
  margin-top: 8px;
}
.skill-recalc-muted {
  color: #6b7280;
  font-weight: 400;
}
.skill-recalc-detail {
  color: #374151;
  line-height: 1.45;
}
.skill-recalc-status {
  color: #4b5563;
  line-height: 1.4;
}
.skill-recalc-error {
  background: #b91c1c;
  color: #ffffff;
  border-radius: 4px;
}
.theme--dark .skill-recalc-progress-card {
  background: #1e1e1e !important;
  border-color: #424242;
}
.theme--dark .skill-recalc-headline,
.theme--dark .skill-recalc-value,
.theme--dark .skill-recalc-match-line,
.theme--dark .skill-recalc-step-label {
  color: rgba(255, 255, 255, 0.92);
}
.theme--dark .skill-recalc-sub-line {
  color: rgba(255, 255, 255, 0.78);
}
.theme--dark .skill-recalc-muted {
  color: rgba(255, 255, 255, 0.55);
}
.theme--dark .skill-recalc-detail {
  color: rgba(255, 255, 255, 0.78);
}
.theme--dark .skill-recalc-status {
  color: rgba(255, 255, 255, 0.65);
}
</style>
